import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  Typography,
  Avatar,
  Button,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import InfoIcon from "@mui/icons-material/Info";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const PaymentSummary = ({
  t,
  isPaid,
  isPayrollDeduct = false,
  totalAmount,
  onTogglePaymentStatus,
  isUpdatingPaymentStatus,
  disableToggle,
  errorMessage,
  onM400Payment,
  isProcessingM400Payment = false,
  onPayrollDeduct,
  isProcessingPayLater = false,
}) => {
  // Parse totalAmount to check if it's zero
  const parseAmount = (amountStr) => {
    if (!amountStr || typeof amountStr !== "string") return 0;
    const numericValue = parseFloat(amountStr.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const amountValue = parseAmount(totalAmount);
  const isZeroAmount = amountValue === 0;

  // Pay Later state: shown when payroll deduct is set AND all orders are settled
  const showPayLater = isPayrollDeduct && isPaid;

  // Determine payment status display
  const paymentStatusColor = isZeroAmount
    ? "#64748b"
    : isPaid
      ? "#10b981" // Green for paid (includes Pay Later)
      : "#ef4444"; // Red for unpaid

  const paymentStatusText = isZeroAmount
    ? t("Console.ActionPopup.NoPaymentRequired", { defaultValue: "No Payment Required" })
    : showPayLater
      ? t("Console.ActionPopup.PayLater", { defaultValue: "Pay Later" })
      : isPaid
        ? t("Console.ActionPopup.Paid")
        : t("Console.ActionPopup.AmountDue");

  const paymentStatusIcon = isZeroAmount ? (
    <InfoIcon />
  ) : showPayLater ? (
    <AccessTimeIcon />
  ) : isPaid ? (
    <CheckCircleIcon />
  ) : (
    <CancelIcon />
  );

  // Disable toggle when amount is zero
  const isToggleDisabled = disableToggle || isUpdatingPaymentStatus || isZeroAmount;
  // M400 and external-pay are also disabled when Pay Later is active
  const isPaymentActionDisabled = isToggleDisabled || showPayLater;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2.5,
          pb: 2,
          borderBottom: "2px solid #f1f5f9",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: paymentStatusColor,
              width: 32,
              height: 32,
            }}
          >
            <PaymentIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.1rem",
              color: "#1e293b",
            }}
          >
            {t("Console.ActionPopup.PaymentInformation", {
              defaultValue: "Payment Information",
            })}
          </Typography>
        </Box>
        {!isZeroAmount && (
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            {onM400Payment && (
              <Button
                variant="contained"
                size="small"
                startIcon={<CreditCardIcon />}
                onClick={onM400Payment}
                disabled={isProcessingM400Payment || isPaymentActionDisabled}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 140,
                  backgroundColor: "#10b981",
                  color: "#ffffff",
                  "&:hover": { backgroundColor: "#059669" },
                  "&:disabled": { backgroundColor: "#cbd5e1", color: "#94a3b8" },
                }}
              >
                {isProcessingM400Payment ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    {t("Console.ActionPopup.Processing", { defaultValue: "Processing..." })}
                  </Box>
                ) : (
                  t("Console.ActionPopup.PayWithM400", { defaultValue: "Pay with M400" })
                )}
              </Button>
            )}
            {onPayrollDeduct && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AccessTimeIcon />}
                onClick={onPayrollDeduct}
                disabled={isProcessingPayLater || isToggleDisabled}
                sx={{
                  textTransform: "none",
                  borderWidth: 2,
                  fontWeight: 600,
                  minWidth: 150,
                  ...(showPayLater
                    ? {
                        borderColor: "#f59e0b",
                        color: "#f59e0b",
                        "&:hover": {
                          borderColor: "#d97706",
                          backgroundColor: "rgba(245,158,11,0.08)",
                        },
                      }
                    : {
                        borderColor: "#ef4444",
                        color: "#ef4444",
                        "&:hover": {
                          borderColor: "#dc2626",
                          backgroundColor: "rgba(239,68,68,0.08)",
                        },
                      }),
                  "&:disabled": { borderColor: "#cbd5e1", color: "#94a3b8" },
                }}
              >
                {isProcessingPayLater ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    {t("Console.ActionPopup.Processing", { defaultValue: "Processing..." })}
                  </Box>
                ) : showPayLater ? (
                  t("Console.ActionPopup.CancelPayLater", { defaultValue: "Cancel Pay Later" })
                ) : (
                  t("Console.ActionPopup.PayLaterButton", { defaultValue: "Pay Later" })
                )}
              </Button>
            )}
            {onTogglePaymentStatus && (
              <Button
                variant="outlined"
                size="small"
                onClick={onTogglePaymentStatus}
                disabled={isPaymentActionDisabled}
                sx={{
                  textTransform: "none",
                  borderWidth: 2,
                  borderColor: "#7c3aed",
                  color: "#7c3aed",
                  fontWeight: 600,
                  minWidth: 150,
                  "&:hover": {
                    borderColor: "#6d28d9",
                    backgroundColor: "rgba(124, 58, 237, 0.08)",
                  },
                  "&:disabled": { borderColor: "#cbd5e1", color: "#94a3b8" },
                }}
              >
                {isUpdatingPaymentStatus ? (
                  <CircularProgress size={18} color="inherit" />
                ) : isPaid ? (
                  t("Console.ActionPopup.MarkAsUnpaid", { defaultValue: "Mark as Unpaid" })
                ) : (
                  t("Console.ActionPopup.MarkAsPaid", { defaultValue: "Pay Externally" })
                )}
              </Button>
            )}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          p: 2.5,
          backgroundColor: isZeroAmount
            ? "#f1f5f9"
            : isPaid
              ? "#f0fdf4" // Green tint for paid (includes Pay Later)
              : "#fef2f2",
          borderRadius: 2,
          border: `2px solid ${paymentStatusColor}`,
          boxShadow: `0 2px 8px ${paymentStatusColor}20`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Chip
            icon={paymentStatusIcon}
            label={paymentStatusText}
              sx={{
              backgroundColor: isZeroAmount
                ? "#e2e8f0"
                : isPaid
                  ? "#d1fae5" // Green chip background (includes Pay Later)
                  : "#fee2e2",
              color: paymentStatusColor,
              fontWeight: 700,
              fontSize: "0.875rem",
              px: 2,
              py: 1,
              height: "auto",
              "& .MuiChip-icon": {
                color: paymentStatusColor,
                fontSize: "1.25rem",
              },
            }}
          />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.875rem",
              color: "#64748b",
            }}
          >
            {t("Console.ActionPopup.AmountTotal")}:
          </Typography>
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.75rem",
            color: paymentStatusColor,
            letterSpacing: "-0.02em",
          }}
        >
          {totalAmount}
        </Typography>
      </Box>
      {errorMessage ? (
        <Typography
          sx={{
            mt: 1.5,
            color: "#dc2626",
            fontSize: "0.85rem",
            fontWeight: 500,
          }}
        >
          {errorMessage}
        </Typography>
      ) : null}
    </Box>
  );
};

PaymentSummary.propTypes = {
  t: PropTypes.func.isRequired,
  isPaid: PropTypes.bool,
  isPayrollDeduct: PropTypes.bool,
  totalAmount: PropTypes.string,
  onTogglePaymentStatus: PropTypes.func,
  isUpdatingPaymentStatus: PropTypes.bool,
  disableToggle: PropTypes.bool,
  errorMessage: PropTypes.string,
  onM400Payment: PropTypes.func,
  isProcessingM400Payment: PropTypes.bool,
  onPayrollDeduct: PropTypes.func,
  isProcessingPayLater: PropTypes.bool,
};

PaymentSummary.defaultProps = {
  isPaid: false,
  isPayrollDeduct: false,
  totalAmount: "$0.00",
  onTogglePaymentStatus: undefined,
  isUpdatingPaymentStatus: false,
  disableToggle: false,
  errorMessage: "",
  onM400Payment: undefined,
  isProcessingM400Payment: false,
  onPayrollDeduct: undefined,
  isProcessingPayLater: false,
};

export default PaymentSummary;





// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/PaymentSummary.jsx